'use client';

import { FC } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { MapPin } from 'lucide-react';
import HotdeskButton from './HotdeskButton';
import RatingBadge from './RatingBadge';
import { ReadOnlyListing } from 'hotdesk_typescript';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { currencyConvert } from '@/hotdesk-ui/helpers/utils';
import { getCitySlug, buildWorkspacePath } from '@/hotdesk-ui/helpers/workspace-url';
import { parseRatingFromMetaData } from '@/hotdesk-ui/helpers/rating.utils';
import { resolveCardAmenityIds } from '@/hotdesk-ui/utils/amenities';
import { useWebReviewsDisplay } from '@/hotdesk-ui/hooks/useWebReviewsDisplay';
import styles from './ExploreNearbySection.module.scss';
import Marquee from 'react-fast-marquee';
import SafeImage from './SafeImage';
import { sizedImageUrl } from '@/hotdesk-ui/helpers/image-url';
export interface ExploreNearbySectionProps {
  listings: ReadOnlyListing[];
  loading?: boolean;
  isRTL?: boolean;
}
const SkeletonCard: FC = () => <div className={styles.card} style={{
  pointerEvents: 'none'
}} data-sentry-component="SkeletonCard" data-sentry-source-file="ExploreNearbySection.tsx">
        <div className={styles.flipInner}>
            <div className={styles.front}>
                <div className={`${styles.imageArea} ${styles.skeleton}`} />
                <div className={styles.content}>
                    <div className={`${styles.skeletonLine} ${styles.skeleton}`} style={{
          width: '70%',
          height: 20
        }} />
                    <div className={`${styles.skeletonLine} ${styles.skeleton}`} style={{
          width: '50%',
          height: 16,
          marginTop: 8
        }} />
                    <div className={styles.separator} />
                    <div className={styles.bottom}>
                        <div className={`${styles.skeletonLine} ${styles.skeleton}`} style={{
            width: 60,
            height: 16
          }} />
                        <div className={`${styles.skeletonLine} ${styles.skeleton}`} style={{
            width: 90,
            height: 20
          }} />
                    </div>
                </div>
            </div>
        </div>
    </div>;
const getObjectKeys = (obj: any): string[] => Object.keys(obj || {});
const getBestPriceUnit = (priceKeys: string[]): string => {
  if (priceKeys.includes('hour')) return 'hour';
  if (priceKeys.includes('day')) return 'day';
  if (priceKeys.includes('month')) return 'month';
  return 'annual';
};
const ExploreNearbyCard: FC<{
  listing: ReadOnlyListing;
}> = ({
  listing
}) => {
  const t = useTranslations('exploreNearby');
  const tCard = useTranslations('landing-v4.card');
  const defaultCurrency = useAppSelector((state: RootState) => state.currency.defaultCurrency);
  const currencyList = useAppSelector((state: RootState) => state.currency.currency);
  const amenitiesMap = useAppSelector((state: RootState) => state.amenities.items);
  const showReviews = useWebReviewsDisplay('listing_home');
  const workspace = listing.workspace;
  const rules = listing.rules || workspace?.rules;
  const images: string[] = rules?.images ? Object.values(rules.images).flat() : [];
  // Amenities – listing first, workspace fallback (same as ListingCard)
  const amenityIds = resolveCardAmenityIds(listing);
  const resolvedAmenities = amenityIds.map(id => amenitiesMap[id]).filter(Boolean);
  const title = `${workspace?.title} ${listing?.title} in ${workspace?.workspaceCity?.name} showing ${!!resolvedAmenities[0] ? resolvedAmenities[0]?.name : ''} ${!!resolvedAmenities[1] ? `, ${resolvedAmenities[1]?.name}` : ''}`;
  const cityName = workspace?.workspaceCity?.name || listing.city?.name || '';
  const countryName = listing.city?.country?.name || '';
  const countrySlug = listing.city?.country?.slug?.toLowerCase() || '';
  const citySlug = getCitySlug(workspace?.workspaceCity || listing.city, cityName);
  const workspaceSlug = workspace?.slug || '';
  const url = `/${buildWorkspacePath({
    country: listing.city?.country,
    countrySlug,
    city: workspace?.workspaceCity || listing.city,
    citySlug,
    workspaceSlug
  })}`;
  const ratingParsed = parseRatingFromMetaData(listing.metaData);
  const rating = ratingParsed.displayRating ? Number(ratingParsed.displayRating) : 0;
  const reviewCount = ratingParsed.count;

  // Price
  const rulesAny = rules as Record<string, unknown> | undefined;
  const priceObj = (rulesAny?.price || {}) as Record<string, number>;
  const priceKeys = getObjectKeys(priceObj);
  const priceUnit = getBestPriceUnit(priceKeys);
  const rawPrice = priceObj[priceUnit] || 0;
  const listingCurrency = (rulesAny?.currency || '') as string;
  let displayPrice = '';
  let displayCurrency = '';
  if (defaultCurrency?.code && rawPrice > 0) {
    const converted = currencyConvert(currencyList, listingCurrency, defaultCurrency.code, rawPrice);
    displayPrice = Math.round(converted).toLocaleString();
    displayCurrency = defaultCurrency.code;
  }
  const locationText = [cityName, countryName].filter(Boolean).join(', ');
  return <div className={styles.card} data-sentry-component="ExploreNearbyCard" data-sentry-source-file="ExploreNearbySection.tsx">
            <div className={styles.flipInner}>
                {/* ── Front face ── */}
                <Link href={url} className={styles.front} target="_blank" rel="noopener noreferrer" data-sentry-element="Link" data-sentry-source-file="ExploreNearbySection.tsx">
                    <div className={styles.imageArea}>
                        {images.length > 0 && <SafeImage src={sizedImageUrl(images[0], 'md')} alt={title} fill className={styles.image} sizes="300px" />}
                        {showReviews && <RatingBadge rating={rating} reviewCount={reviewCount} />}
                    </div>

                    <div className={styles.content}>
                        <h3 className={styles.name} dir="auto">{title}</h3>
                        {locationText && <p className={styles.location}>
                                <MapPin size={14} className={styles.locationIcon} />
                                {locationText}
                            </p>}

                        <div className={styles.separator} />

                        <div className={styles.bottom}>
                            {resolvedAmenities.length > 0 && <div className={styles.amenities}>
                                    {resolvedAmenities.slice(0, 3).map(amenity => amenity.url ? <span key={amenity.id} className={styles.amenityIcon} title={amenity.name}>
                                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                                <img src={amenity.url} alt={amenity.name} width={16} height={16} className={styles.amenityImg} />
                                            </span> : null)}
                                </div>}
                            {displayPrice && <span className={styles.price}>
                                    {displayCurrency} {displayPrice}<span>/{tCard(priceUnit)}</span>
                                </span>}
                        </div>
                    </div>
                </Link>

                {/* ── Back face ── */}
                <div className={styles.back}>
                    <h3 className={styles.backTitle}>{title}</h3>

                    {resolvedAmenities.length > 0 && <div className={styles.backAmenities}>
                            {resolvedAmenities.slice(0, 4).map(amenity => <div key={amenity.id} className={styles.backAmenityItem}>
                                    {amenity.url &&
            // eslint-disable-next-line @next/next/no-img-element
            <img src={amenity.url} alt="" width={16} height={16} className={styles.backAmenityImg} />}
                                    <span className={styles.backAmenityName}>{amenity.name}</span>
                                </div>)}
                        </div>}

                    <HotdeskButton as="next-link" href={url} variant="primary" size="md" fullWidth target="_blank" rel="noopener noreferrer" className={styles.backCta} data-sentry-element="HotdeskButton" data-sentry-source-file="ExploreNearbySection.tsx">
                        {t('viewWorkspace')}
                    </HotdeskButton>

                    {displayPrice && <div className={styles.backPriceBox}>
                            <span className={styles.backPriceLabel}>{t('startingFrom')}</span>
                            <span className={styles.backPriceValue}>
                                {displayCurrency} {displayPrice}/{tCard(priceUnit)}
                            </span>
                        </div>}
                </div>
            </div>
        </div>;
};
const ExploreNearbySection: FC<ExploreNearbySectionProps> = ({
  listings,
  loading = false,
  isRTL = false
}) => {
  const t = useTranslations('exploreNearby');
  const showSkeleton = loading && listings.length === 0;
  if (!showSkeleton && (!listings || listings.length === 0)) return null;
  return <section className={styles.section} data-sentry-component="ExploreNearbySection" data-sentry-source-file="ExploreNearbySection.tsx">
            <div className={styles.header}>
                <h2 className={styles.title}>{t('title')}</h2>
                <p className={styles.subtitle}>{t('subtitle')}</p>
            </div>

            <div className={styles.track} dir={isRTL ? 'rtl' : 'ltr'}>
                {showSkeleton ? Array.from({
        length: 20
      }).map((_, i) => <SkeletonCard key={i} />) : <div className={styles.marquee}>
                             <Marquee pauseOnHover direction="right" speed={40}>
                                {listings.map((listing, idx) => <ExploreNearbyCard key={listing.id || idx} listing={listing} />)}
                                
                             </Marquee>
                        </div>}
            </div>

            {!showSkeleton && <div className={styles.seeMoreWrapper}>
                    <HotdeskButton as="next-link" href="/workspaces" variant="secondary" size="md" className={styles.seeMoreBtn} onClick={() => window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })}>
                        {t('seeMore')}
                    </HotdeskButton>
                </div>}
        </section>;
};
export default ExploreNearbySection;